import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import { BentoBrand, BentoBrandProduct } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

import { FormState, ProductRefKind, ValidationKey } from "./schema";

interface Props {
  state: FormState;
  setState: (next: FormState) => void;
  bentoBrand: BentoBrand;
  errorKey?: ValidationKey;
}

const optionRowSx = {
  border: "1px solid rgba(0,0,0,0.1)",
  borderRadius: "8px",
  px: 1.25,
  py: 0.75,
  width: "100%",
  minWidth: 0,
  overflow: "hidden",
};

const optionLabelSx = {
  m: 0,
  py: 0.5,
  "& .MuiFormControlLabel-label": { ml: 0.5 },
};

const compactCheckboxSx = { p: 0.25 };

const subControlSx = { pl: 3.5, mt: 0.5, mb: 1 };

const ERROR_BORDER = "1px solid #c0392b";

export default function IncludeOptions({
  state,
  setState,
  bentoBrand,
  errorKey,
}: Props) {
  const { getToken } = useAuth();
  const { profile, currentOrg } = useContext(OrganizationUserContext);
  const [products, setProducts] = useState<BentoBrandProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);

  const productRowRef = useRef<HTMLDivElement>(null);
  const caseStudyRowRef = useRef<HTMLDivElement>(null);
  const contentIdeaRowRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const caseStudyInputRef = useRef<HTMLTextAreaElement>(null);
  const contentIdeaInputRef = useRef<HTMLTextAreaElement>(null);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState({ ...state, [key]: value });

  useEffect(() => {
    if (!errorKey) return;
    const refs: Record<
      NonNullable<ValidationKey>,
      {
        row: React.RefObject<HTMLDivElement>;
        input: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
      }
    > = {
      product: { row: productRowRef, input: productInputRef },
      case_study: { row: caseStudyRowRef, input: caseStudyInputRef },
      content_idea: { row: contentIdeaRowRef, input: contentIdeaInputRef },
    };
    const target = refs[errorKey];
    if (!target) return;
    target.row.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => target.input.current?.focus(), 250);
  }, [errorKey]);

  useEffect(() => {
    if (
      !state.referenceProduct ||
      productsLoaded ||
      productsLoading ||
      !currentOrg?.id ||
      !bentoBrand?.id
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      setProductsLoading(true);
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/bento-brands/${bentoBrand.id}/products?must_have_image_url=false&page=1&per_page=25`,
        );
        if (cancelled) return;
        const list: BentoBrandProduct[] = res?.bentoBrandProducts ?? [];
        setProducts(list);
        setProductsLoaded(true);
        if (list.length > 0 && state.productRef.kind === "ai_find") {
          setState({
            ...state,
            productRef: { kind: "existing" },
          });
        }
      } catch {
        if (!cancelled) setProductsLoaded(true);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.referenceProduct, currentOrg?.id, bentoBrand?.id]);

  return (
    <Stack spacing={0.5}>
      {/* Instagram */}
      <Box sx={optionRowSx}>
        <FormControlLabel
          sx={optionLabelSx}
          control={
            <Checkbox
              checked={state.referenceInstagram}
              onChange={(e) => setField("referenceInstagram", e.target.checked)}
              size="small"
              sx={compactCheckboxSx}
            />
          }
          label="Reference their Instagram page"
        />
      </Box>

      {/* Product */}
      <Box
        ref={productRowRef}
        sx={{
          ...optionRowSx,
          ...(errorKey === "product" && { border: ERROR_BORDER }),
        }}
      >
        <FormControlLabel
          sx={optionLabelSx}
          control={
            <Checkbox
              checked={state.referenceProduct}
              onChange={(e) => setField("referenceProduct", e.target.checked)}
              size="small"
              sx={compactCheckboxSx}
            />
          }
          label="Reference a specific product"
        />
        {state.referenceProduct && (
          <Box sx={subControlSx}>
            {productsLoading ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 1 }}
              >
                <CircularProgress size={14} sx={{ color: "#8084FF" }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Loading saved products…
                </Typography>
              </Stack>
            ) : (
              <RadioGroup
                value={state.productRef.kind}
                onChange={(e) => {
                  const kind = e.target.value as ProductRefKind;
                  setField("productRef", { kind });
                }}
              >
                {products.length > 0 && (
                  <>
                    <FormControlLabel
                      value="existing"
                      control={<Radio size="small" />}
                      label="Pick from this brand's products"
                    />
                    {state.productRef.kind === "existing" && (
                      <Box
                        sx={{
                          pl: 4,
                          mt: 0.5,
                          mb: 1,
                          width: "100%",
                          minWidth: 0,
                          display: "flex",
                          gap: 1,
                          overflowX: "auto",
                          overflowY: "hidden",
                          pb: 1,
                          "&::-webkit-scrollbar": { height: 6 },
                          "&::-webkit-scrollbar-thumb": {
                            background: "rgba(0,0,0,0.15)",
                            borderRadius: 3,
                          },
                        }}
                      >
                        {products.slice(0, 25).map((p) => {
                          const selected = state.productRef.productId === p.id;
                          return (
                            <Box
                              key={p.id}
                              onClick={() =>
                                setField("productRef", {
                                  kind: "existing",
                                  productId: p.id,
                                })
                              }
                              sx={{
                                width: 160,
                                flexShrink: 0,
                                p: 1,
                                borderRadius: 1,
                                cursor: "pointer",
                                border: `1px solid ${
                                  selected ? "#8084FF" : "rgba(0,0,0,0.1)"
                                }`,
                                background: selected
                                  ? "rgba(128,132,255,0.08)"
                                  : "white",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {p.imageUrl ? (
                                <Box
                                  component="img"
                                  src={p.imageUrl}
                                  alt={p.name}
                                  sx={{
                                    width: "100%",
                                    height: 60,
                                    borderRadius: 1,
                                    objectFit: "cover",
                                    background: "#f3f3f5",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: 60,
                                    borderRadius: 1,
                                    background: "#f3f3f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Box
                                    component="i"
                                    className="fa-regular fa-box"
                                    sx={{ color: "#9ca3af", fontSize: 16 }}
                                  />
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  fontWeight: 600,
                                  mt: 0.75,
                                  lineHeight: 1.3,
                                  wordBreak: "break-word",
                                }}
                              >
                                {p.name}
                              </Typography>
                              {p.description && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    lineHeight: 1.3,
                                    mt: 0.25,
                                  }}
                                >
                                  {p.description}
                                </Typography>
                              )}
                              {p.onlineStoreUrl && (
                                <Box
                                  component="a"
                                  href={p.onlineStoreUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mt: 0.5,
                                    fontSize: 11,
                                    color: "#8084FF",
                                    textDecoration: "none",
                                    fontWeight: 500,
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  View
                                  <Box
                                    component="i"
                                    className="fa-regular fa-arrow-up-right-from-square"
                                    sx={{ fontSize: 9 }}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
                <FormControlLabel
                  value="ai_find"
                  control={<Radio size="small" />}
                  label="Let AI find a product to reference"
                />
                <FormControlLabel
                  value="user_input"
                  control={<Radio size="small" />}
                  label="I'll enter a product name or URL"
                />
                {state.productRef.kind === "user_input" && (
                  <TextField
                    inputRef={productInputRef}
                    fullWidth
                    size="small"
                    placeholder="Product name or URL"
                    value={state.productRef.userInput ?? ""}
                    onChange={(e) =>
                      setField("productRef", {
                        kind: "user_input",
                        userInput: e.target.value,
                      })
                    }
                    sx={{ ml: 4, mt: 0.5, width: "calc(100% - 32px)" }}
                  />
                )}
              </RadioGroup>
            )}
          </Box>
        )}
      </Box>

      {/* Case study */}
      <Box
        ref={caseStudyRowRef}
        sx={{
          ...optionRowSx,
          ...(errorKey === "case_study" && { border: ERROR_BORDER }),
        }}
      >
        <FormControlLabel
          sx={optionLabelSx}
          control={
            <Checkbox
              checked={state.mentionCaseStudy}
              onChange={(e) => {
                const checked = e.target.checked;
                const prefill =
                  profile?.caseStudy ??
                  profile?.portfolioContent?.case_study ??
                  "";
                setState({
                  ...state,
                  mentionCaseStudy: checked,
                  caseStudyText:
                    checked && !state.caseStudyText
                      ? prefill
                      : state.caseStudyText,
                });
              }}
              size="small"
              sx={compactCheckboxSx}
            />
          }
          label="Mention my past case study"
        />
        {state.mentionCaseStudy && (
          <Box sx={subControlSx}>
            <TextField
              inputRef={caseStudyInputRef}
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              size="small"
              placeholder="Write or edit the case study you'd like to mention..."
              value={state.caseStudyText}
              onChange={(e) => setField("caseStudyText", e.target.value)}
            />
            {profile?.mostImpressivePartnerships && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                Past brands on file: {profile.mostImpressivePartnerships}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Content idea */}
      <Box
        ref={contentIdeaRowRef}
        sx={{
          ...optionRowSx,
          ...(errorKey === "content_idea" && { border: ERROR_BORDER }),
        }}
      >
        <FormControlLabel
          sx={optionLabelSx}
          control={
            <Checkbox
              checked={state.contentIdea}
              onChange={(e) => setField("contentIdea", e.target.checked)}
              size="small"
              sx={compactCheckboxSx}
            />
          }
          label="Provide a content idea relevant for the brand"
        />
        {state.contentIdea && (
          <Box sx={subControlSx}>
            <RadioGroup
              value={state.contentIdeaState.kind}
              onChange={(e) =>
                setField("contentIdeaState", {
                  kind: e.target.value as "ai_generate" | "user_text",
                  text: state.contentIdeaState.text,
                })
              }
            >
              <FormControlLabel
                value="ai_generate"
                control={<Radio size="small" />}
                label="Let AI generate one"
              />
              <FormControlLabel
                value="user_text"
                control={<Radio size="small" />}
                label="I'll write one"
              />
            </RadioGroup>
            {state.contentIdeaState.kind === "user_text" && (
              <TextField
                inputRef={contentIdeaInputRef}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                placeholder="e.g., A reel comparing your products to my morning routine..."
                value={state.contentIdeaState.text ?? ""}
                onChange={(e) =>
                  setField("contentIdeaState", {
                    kind: "user_text",
                    text: e.target.value,
                  })
                }
              />
            )}
          </Box>
        )}
      </Box>

      {/* Audience alignment */}
      <Box sx={optionRowSx}>
        <FormControlLabel
          sx={optionLabelSx}
          control={
            <Checkbox
              checked={state.audienceAlignment}
              onChange={(e) => setField("audienceAlignment", e.target.checked)}
              size="small"
              sx={compactCheckboxSx}
            />
          }
          label="Talk about how my audience aligns with the brand's target"
        />
      </Box>
    </Stack>
  );
}
